import Plotly from "plotly.js-dist-min";
import usdaLogoUrl from "@fsa-style/src/img/usda-logo.svg";

import { renderGrowl } from "@js/nwcc-growl.js";
import { resizePlot } from "@js/nwcc-plotting.js";
import {
    setBookmarkTextBox,
    attachStationMetaModalButton,
    attachElementMetaModalButton,
    scrollToOutput,
    renderInvalidStationGrowl,
    renderInvalidElementGrowl,
} from "../controller/home.js";
import * as getData from "../model/get-data.js";

export const awdbPlotId = "awdb--plot";
const extremi = ["min", "max"];

export const diskIcon = {
    width: 857.1,
    height: 1000,
    path: "m214-7h429v214h-429v-214z m500 0h72v500q0 8-6 21t-11 20l-157 156q-5 6-19 12t-22 5v-232q0-22-15-38t-38-16h-322q-22 0-37 16t-16 38v232h-72v-714h72v232q0 22 16 38t37 16h465q22 0 38-16t15-38v-232z m-214 518v178q0 8-5 13t-13 5h-107q-7 0-13-5t-5-13v-178q0-8 5-13t13-5h107q7 0 13 5t5 13z m357-18v-518q0-22-15-38t-38-16h-750q-23 0-38 16t-16 38v750q0 22 16 38t38 16h517q23 0 50-12t42-26l156-157q16-15 27-42t11-49z",
    transform: "matrix(1 0 0 -1 0 850)",
};

export async function makePlot(stationOveride, elementOveride) {
    const station = document.getElementById("select--station").value
        ? document.getElementById("select--station").value
        : stationOveride;
    const element = document.getElementById("select--element").value
        ? document.getElementById("select--element").value
        : elementOveride;
    if (!station) {
        renderInvalidStationGrowl();
    } else if (!element) {
        renderInvalidElementGrowl();
    } else {
        await getData.updateSelectedStationData();
        await updatePlot();
        scrollToOutput();
        setBookmarkTextBox();
    }
}

export function getTraceColor(functionCode, physicalElementName) {
    const isTemperature = physicalElementName.toLowerCase().includes("temperature");
    let code = functionCode;
    if (isTemperature) {
        if (functionCode === "X") {
            code = "N";
        } else if (functionCode === "N") {
            code = "X";
        }
    }
    const colorLookup = {
        X: "rgba(0,0,237,0.4)", // max
        V: "rgba(0,237,0,0.4)", // central tendency
        N: "rgba(237,0,0,0.4)", // min
    };
    return colorLookup[code] || null;
}

export async function updatePlot(plotId = awdbPlotId) {
    const awdbGrid = document.getElementById("awdb--grid");
    awdbGrid.className = "fsa-grid__1/1 fsa-p--s";
    const stationMeta = getData.getSelectedStationMeta();
    const elementsLookup = getData.getRefData().elements;
    const numElements = getData.getStationDataElementsLength();
    if (numElements === 0) {
        renderGrowl(
            "No data returned!",
            "No data was found for the request, please try a different query.",
            "error",
            true,
            10,
        );
    }
    const outputDiv = document.getElementById("awdb--output");
    outputDiv.innerHTML = "";
    const graphContainer = document.createElement("div");
    graphContainer.className = "graph-container";
    outputDiv.appendChild(graphContainer);
    const plotDiv = document.createElement("div");
    plotDiv.id = plotId;
    graphContainer.appendChild(plotDiv);
    const plotData = [];
    const duration = document.getElementById("select--duration")?.value || "DAILY";
    const stationLabel = `${stationMeta.name} (${stationMeta.stationTriplet})`;
    const plotType = ["HOURLY", "DAILY"].includes(duration) ? "scatter" : "bar";
    let axisNum = 0;
    const unitsObj = {};
    const plotTypes = [];
    for (let elemIdx = 0; elemIdx < numElements; elemIdx++) {
        const stationElement = getData.getSelectedStationElement(elemIdx);
        const elementCode = stationElement.elementCode;
        const unitCode = stationElement.storedUnitCode;
        const functionCode = elementsLookup[stationElement.elementCode].functionCode;
        const physicalElementName = elementsLookup[stationElement.elementCode].physicalElementName;
        let elementLabel = `${elementCode} (${unitCode})`;
        const depth = stationElement.heightDepth;
        const usePlotType = elementCode === "PRCP" ? "bar" : plotType;
        if (typeof depth !== "undefined") {
            elementLabel = `${stationElement.elementCode} @ ${depth}" (${unitCode})`;
        }
        if (!Object.keys(unitsObj).includes(unitCode)) {
            axisNum += 1;
            plotTypes.push(usePlotType);
        } else {
            axisNum = unitsObj[unitCode].axisNum;
        }
        const currUnits = Object.keys(unitsObj);
        const axisLabel = currUnits.includes(unitCode) ? [...unitsObj[unitCode].label, elementCode] : [elementCode];
        unitsObj[unitCode] = { axisNum: axisNum, label: axisLabel };
        const duration = stationElement.durationName;
        const elementValues = getData.getStationDataValues(elemIdx);
        const traceMode = "lines";

        let elementTrace = {
            x: elementValues.map((value) => getData.parseApiReturnedDate(value, duration)),
            y: elementValues.map((value) => value.value),
            mode: traceMode,
            type: usePlotType,
            name: elementLabel,
            visible: true, //axisNum > 1 ? "legendonly" : true,
        };
        const traceColor = getTraceColor(functionCode, physicalElementName);
        if (traceColor) {
            elementTrace.line = { color: traceColor };
        }
        if (axisNum > 1) {
            const nextAxis = { yaxis: `y${axisNum}` };
            elementTrace = { ...elementTrace, ...nextAxis };
        }
        plotData.push(elementTrace);
    }
    const layout = getPlotLayout(stationLabel, duration, unitsObj);
    const config = getPlotConfig();
    await Plotly.newPlot(plotId, plotData, layout, config);
    resizePlot();
    const multiAxes = axisNum > 1;
    addPlotControls(outputDiv, plotId, plotTypes, multiAxes);
}

export function addPlotControls(outputDiv, plotId, plotTypes, multiAxes = false) {
    const plotDiv = document.getElementById(plotId);
    plotDiv.style = "width:auto; height: 100%";
    const ctrlBox = document.createElement("div");
    ctrlBox.className = "fsa-box@l fsa-box--br-2 fsa-m--xxs fsa-p--xxs";
    outputDiv.appendChild(ctrlBox);
    const fieldSet = document.createElement("fieldset");
    ctrlBox.appendChild(fieldSet);
    const plotControls = document.createElement("div");
    plotControls.className = "fsa-level@m fsa-level--justify-around@m";
    const plotControlsTop = document.createElement("div");
    plotControlsTop.className = "fsa-level fsa-level--grow-auto";
    fieldSet.appendChild(plotControls);
    plotControls.appendChild(plotControlsTop);
    attachStationMetaModalButton(plotControlsTop);
    attachElementMetaModalButton(plotControlsTop);
    plotControlsTop.className = "fsa-level fsa-level--grow-auto";
    const plotControlsMiddle = document.createElement("div");
    plotControlsMiddle.className = "fsa-level fsa-level--grow-auto";
    plotControls.appendChild(plotControlsMiddle);

    const axesUsed = [true, multiAxes];
    axesUsed.forEach(function (usedAxis, idx) {
        if (usedAxis) {
            const plotType = plotTypes[idx];
            const plotTypeRadio = getPlotTypeRadio(plotType, idx);
            plotControlsMiddle.appendChild(plotTypeRadio);
            attachPlotTypeRadioListeners(plotId, idx);
        }
    });

    const plotControlsBottom = document.createElement("div");
    plotControls.appendChild(plotControlsBottom);
    plotControlsBottom.className = "fsa-level fsa-level--grow-auto";
    const yAxisMax = plotDiv.layout?.yaxis?.range[1] || 1;
    const yAxisMin = plotDiv.layout?.yaxis?.range[0] || 0;
    addYAxisControls(plotControlsBottom, plotDiv, yAxisMin, yAxisMax);
}

export function addYAxisControls(targetDiv, plotDiv, yAxisMin, yAxisMax) {
    extremi.forEach(function (extremum) {
        const extremumVal = extremum === "max" ? yAxisMax : yAxisMin;
        const yAxisSpin = document.createElement("div");
        yAxisSpin.className = "fsa-field";
        yAxisSpin.innerHTML = /*html*/ `
            <label class="fsa-field__label" for="plot-yaxis-range-max">y-axis ${extremum}</label>
            <div class="fsa-field__item">
                <div class="fsa-spinbox fsa-spinbox--inline fsa-spinbox--small">
                    <div class="fsa-spinbox__number">
                        <input class="fsa-input fsa-spinbox__input" id="plot-yaxis-range-${extremum}" aria-describedby="plot-yaxis-range-${extremum}-help" name="plot-yaxis-range-${extremum}" type="number" value="${Math.round(extremumVal)}" step="1">
                    </div>
                    <div class="fsa-spinbox__actions" aria-hidden="true">
                        <button tabindex="-1" class="fsa-spinbox__btn fsa-spinbox__btn--increment" type="button" data-behavior="spinbox-spin" title="Increase"></button>
                        <button tabindex="-1" class="fsa-spinbox__btn fsa-spinbox__btn--decrement" type="button" data-behavior="spinbox-spin" title="Decrease"></button>
                    </div>
                </div>
            </div>
            <span class="fsa-field__help" id="plot-yaxis-range-max-help">Change the y-axis ${extremum}</span>`;

        targetDiv.appendChild(yAxisSpin);
        addYAxisControlListeners(yAxisSpin, plotDiv, extremum);
    });
}

export function addYAxisControlListeners(yAxisSpin, plotDiv, extremum) {
    const yAxisSpinCtrls = yAxisSpin.querySelectorAll('[data-behavior~="spinbox-spin"]');
    yAxisSpinCtrls.forEach(function (spinboxTrigger) {
        spinboxTrigger.addEventListener("click", (event) => {
            incrementYAxisRange(event, plotDiv, extremum);
        });
    });
    const targetSpinner = document.getElementById(`plot-yaxis-range-${extremum}`);
    targetSpinner.addEventListener("change", () => {
        setYAxisRange(plotDiv, extremum);
    });
}

export function incrementYAxisRange(event, plotDiv, extremum) {
    const targetSpinner = document.getElementById(`plot-yaxis-range-${extremum}`);
    if (event.currentTarget.classList.contains("fsa-spinbox__btn--increment")) {
        targetSpinner.stepUp();
    } else {
        targetSpinner.stepDown();
    }
    const yAxisRange = plotDiv.layout?.yaxis?.range || [0, 1];
    const newRange =
        extremum === "max"
            ? [yAxisRange[0], parseFloat(targetSpinner.value)]
            : [parseFloat(targetSpinner.value), yAxisRange[1]];
    Plotly.relayout(plotDiv.id, { "yaxis.range": newRange });
}

export function setYAxisRange(plotDiv, extremum) {
    const targetSpinner = document.getElementById(`plot-yaxis-range-${extremum}`);
    const yAxisRange = plotDiv.layout?.yaxis?.range || [0, 1];
    const newRange =
        extremum === "max"
            ? [yAxisRange[0], parseFloat(targetSpinner.value)]
            : [parseFloat(targetSpinner.value), yAxisRange[1]];
    Plotly.relayout(plotDiv.id, { "yaxis.range": newRange });
}

export function getPlotTypeRadio(plotType = "scatter", axis = 0) {
    const side = !axis ? "Left" : "Right";
    const lineId = `plot-type--line-${side.toLowerCase()}`;
    const barId = `plot-type--bar-${side.toLowerCase()}`;
    const lineChecked = plotType === "scatter" ? "checked" : "";
    const barChecked = plotType === "bar" ? "checked" : "";
    const plotTypeRadio = document.createElement("div");
    plotTypeRadio.className = "fsa-field";
    plotTypeRadio.innerHTML = /*html*/ `
        <label class="fsa-field__label" id="radio--plot-type-label-${side.toLowerCase()}">${side} Axis</label>
        <div class="fsa-level" aria-labelledby="radio--plot-type-label-${side.toLowerCase()}" aria-describedby="radio--plot-type-help-${side.toLowerCase()}">
            <span>
                <input class="fsa-radio" id="${lineId}" type="radio" name="plot-type-${side.toLowerCase()}" ${lineChecked}>
                <label for="plot-type--line-${side.toLowerCase()}">Line</label>
            </span>
            <span>
                <input class="fsa-radio" id="${barId}" type="radio" name="plot-type-${side.toLowerCase()}" ${barChecked}>
                <label for="plot-type--bar-${side.toLowerCase()}">Bar</label>
            </span>
        </div>
        <span class="fsa-field__help" id="help--radio-plot-type-${side.toLowerCase()}">Select a ${side.toLowerCase()} axis plot type</span>`;

    return plotTypeRadio;
    // TODO: add logic for indeterminate setting, when scatter and bars are mixed on page load
}

export function attachPlotTypeRadioListeners(plotId, axis = 0) {
    const side = !axis ? "left" : "right";
    const lineId = `plot-type--line-${side}`;
    const linePlotToggle = document.getElementById(lineId);
    linePlotToggle.addEventListener(
        "change",
        () => {
            Plotly.restyle(plotId, { type: "scatter" });
        },
        false,
    );
    const barId = `plot-type--bar-${side}`;
    const barPlotToggle = document.getElementById(barId);
    barPlotToggle.addEventListener(
        "change",
        () => {
            Plotly.restyle(plotId, { type: "bar" });
        },
        false,
    );
}

export function getPlotLayout(stationLabel, duration, unitsObj) {
    const dateFormats = {
        INSTANTANEOUS: "%m/%d/%Y %H:%M",
        HOURLY: "%m/%d/%Y %H:%M",
        DAILY: "%m/%d/%Y",
        SEMIMONTHLY: "%m/%d/%Y",
        MONTHLY: "%b %Y",
        WATER_YEAR: "%Y",
        CALENDAR_YEAR: "%Y",
    };
    const yAxisObj = {};
    Object.keys(unitsObj).forEach(function (unit) {
        const axisNum = unitsObj[unit].axisNum;
        const axisLabel = unitsObj[unit].label.join(", ");
        const axisKey = axisNum > 1 ? `yaxis${axisNum}` : "yaxis";
        let axisOptions = { title: `${axisLabel} (${unit})` };
        if (axisNum > 1) {
            const nextAxisOptions = {
                overlaying: "y",
                side: "right",
                showgrid: false,
                zeroline: false,
            };
            if (axisNum > 2) {
                nextAxisOptions.autoshift = true;
                nextAxisOptions.anchor = "free";
            }
            axisOptions = { ...axisOptions, ...nextAxisOptions };
        } else {
            const primaryAxisOptions = { showgrid: true, zeroline: true, autorange: true };
            if (unit.toLowerCase() === "in") {
                primaryAxisOptions.autorangeoptions = { minallowed: 0 };
            }
            axisOptions = { ...axisOptions, ...primaryAxisOptions };
        }
        yAxisObj[axisKey] = axisOptions;
    });
    const layout = {
        template: "plotly_white",
        autosize: true,
        title: { text: stationLabel, xanchor: "left", x: 0, xref: "container" },
        showlegend: true,
        legend: {
            // tracegroupgap: 1,
            // bordercolor: "#E2E2E2",
            // borderwidth: 1,
            orientation: "h",
            bgcolor: "rgba(0,0,0,0)",
            // xanchor: "left",
            // yanchor: "top",
            // x: 1,
            // y: 0
        },
        hovermode: "x unified",
        ...yAxisObj,
        xaxis: {
            type: "date",
            tickformat: dateFormats[duration.toUpperCase()],
            hoverformat: dateFormats[duration.toUpperCase()],
        },
        margin: { b: 50, t: 50, l: 50, r: 50, pad: 5 },
        images: [
            {
                source: usdaLogoUrl,
                xref: "paper",
                yref: "paper",
                x: -0.075,
                y: 0.99,
                xanchor: "left",
                yanchor: "top",
                sizex: 0.23,
                sizey: 0.15,
                sizing: "contain",
                opacity: 0.5,
                layer: "below",
            },
        ],
    };
    return layout;
}

export function getPlotConfig() {
    const config = {
        displaylogo: false,
        modeBarButtonsToAdd: [
            {
                name: "downloadCsv",
                icon: diskIcon,
                title: "Download data as CSV",
                click: exportPlotlyChartDataToCSV,
            },
        ],
        responsive: true,
    };
    return config;
}

export function exportPlotlyChartDataToCSV(plotlyChart) {
    const seriesNames = plotlyChart.data.map((series) => series.name);
    const dataRows = ["Date," + seriesNames.join(",")];

    for (let i = 0; i <= plotlyChart.data[0].x.length; ++i) {
        const curRow = new Array();
        const dateStr = plotlyChart.data[0].x[i];
        curRow.push(dateStr);
        for (let j = 0; j < seriesNames.length; ++j) {
            curRow.push(plotlyChart.data[j].y[i] !== null ? plotlyChart.data[j].y[i] : "");
        }
        dataRows.push(curRow.join(","));
    }
    const blob = new Blob([dataRows.join("\r\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const reportType = document.getElementById("select--element")?.value || "unknown report";
    const station = document.getElementById("select--station")?.value || "unknown station";
    const filename = `${station.replaceAll(":", "_").toLowerCase()}--${reportType.replaceAll(",", "_").toLowerCase()}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
